import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import Icon from '../AppIcon';
import { useAuth } from '../../contexts/AuthContext';

const Header = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, signOut } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);

  const navigationItems = [
    { label: 'Executive View', path: '/executive-view', icon: 'LayoutDashboard' },
    { label: 'Data Drivers', path: '/drill-down-analysis', icon: 'TrendingUp' },
    { label: 'Administration', path: '/administration-panel', icon: 'Settings' },
  ];

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(event?.target)) {
        setUserDropdownOpen(false);
      }
    };

    const handleEscape = (event) => {
      if (event?.key === 'Escape') {
        setUserDropdownOpen(false);
        setMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
    };
  }, []);

  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [mobileMenuOpen]);

  const handleNavigation = (path) => {
    navigate(path);
    setMobileMenuOpen(false);
  };

  const handleLogout = async () => {
    try {
      await signOut();
      navigate('/login');
      setUserDropdownOpen(false);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const isActive = (path) => location?.pathname === path;

  const getInitials = (name) => {
    return name?.split(' ')?.map((n) => n?.[0])?.join('')?.toUpperCase()?.slice(0, 2);
  };

  const displayName = userProfile?.fullName || user?.email?.split('@')?.[0] || 'User';
  const displayEmail = userProfile?.email || user?.email || '';
  const displayRole = userProfile?.role?.toUpperCase() || 'USER';

  return (
    <>
      <header className="fixed top-0 left-0 right-0 z-[100] bg-card shadow-md">
        <div className="flex items-center justify-between h-16 px-6">
          <div className="flex items-center gap-8">
            <div
              className="flex items-center gap-3 cursor-pointer"
              onClick={() => handleNavigation('/executive-view')}
            >
              <img
                src="/assets/images/ORIN_LOGO-1777609908143.png"
                alt="ORIN Logo"
                className="h-10 w-auto object-contain"
              />
              <span className="text-xl font-heading font-semibold text-foreground">
                ORIN
              </span>
            </div>

            <nav className="hidden lg:flex items-center gap-1">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-md font-body font-medium transition-smooth hover-lift ${
                    isActive(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:bg-muted hover:text-foreground'
                  }`}
                >
                  <Icon name={item?.icon} size={18} />
                  <span>{item?.label}</span>
                </button>
              ))}
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setUserDropdownOpen(!userDropdownOpen)}
                className="flex items-center gap-3 px-3 py-2 rounded-md hover:bg-muted transition-smooth focus-ring"
                aria-label="User menu"
                aria-expanded={userDropdownOpen}
              >
                <div className="flex items-center justify-center w-9 h-9 rounded-full bg-primary text-primary-foreground font-caption font-semibold">
                  {getInitials(displayName)}
                </div>
                <div className="hidden md:block text-left">
                  <div className="text-sm font-medium text-foreground">
                    {displayName}
                  </div>
                  <div className="text-xs text-muted-foreground font-caption">
                    {displayRole}
                  </div>
                </div>
                <Icon
                  name="ChevronDown"
                  size={16}
                  className={`transition-smooth ${
                    userDropdownOpen ? 'rotate-180' : ''
                  }`}
                />
              </button>

              {userDropdownOpen && (
                <div className="absolute right-0 top-full mt-2 w-64 bg-popover rounded-md shadow-lg border border-border overflow-hidden z-[110]">
                  <div className="p-4 border-b border-border">
                    <div className="font-medium text-foreground">
                      {displayName}
                    </div>
                    <div className="text-sm text-muted-foreground font-caption">
                      {displayEmail}
                    </div>
                  </div>
                  <div className="py-2">
                    <button
                      onClick={() => {
                        navigate('/administration-panel');
                        setUserDropdownOpen(false);
                      }}
                      className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm hover:bg-muted transition-smooth"
                    >
                      <Icon name="User" size={16} />
                      <span>Profile Settings</span>
                    </button>
                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 w-full px-4 py-2 text-left text-sm text-destructive hover:bg-destructive/10 transition-smooth"
                    >
                      <Icon name="LogOut" size={16} />
                      <span>Logout</span>
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              className="lg:hidden p-2 rounded-md hover:bg-muted transition-smooth focus-ring"
              aria-label="Toggle mobile menu"
              aria-expanded={mobileMenuOpen}
            >
              <Icon name={mobileMenuOpen ? 'X' : 'Menu'} size={24} />
            </button>
          </div>
        </div>
      </header>
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-[200] lg:hidden">
          <div
            className="absolute inset-0 bg-background"
            onClick={() => setMobileMenuOpen(false)}
          />
          <div className="absolute top-16 left-0 right-0 bottom-0 bg-card overflow-y-auto">
            <nav className="p-4 space-y-2">
              {navigationItems?.map((item) => (
                <button
                  key={item?.path}
                  onClick={() => handleNavigation(item?.path)}
                  className={`flex items-center gap-3 w-full px-4 py-3 rounded-md font-body font-medium transition-smooth ${
                    isActive(item?.path)
                      ? 'bg-primary text-primary-foreground'
                      : 'text-foreground hover:bg-muted'
                  }`}
                >
                  <Icon name={item?.icon} size={20} />
                  <span>{item?.label}</span>
                </button>
              ))}
            </nav>
          </div>
        </div>
      )}
    </>
  );
};

export default Header;